/**
 * Tri-platform tracking for the promo / influencer campaign system.
 *
 * Per the project rule, every conversion-shaped event must fire to all
 * three: GA (firebase analytics), Facebook Pixel (CAPI-paired), and
 * Pinterest Tag. These helpers wrap the existing utils so call-sites only
 * need to invoke a single function per event.
 */

import { logEvent } from 'firebase/analytics';
import { analytics } from '../firebaseApp';
import { isAnalyticsEnabled } from './isAnalyticsEnabled';

const isFbqAvailable = () => typeof window !== 'undefined' && window.fbq;
const isPintrkAvailable = () => typeof window !== 'undefined' && window.pintrk;

const sendGa = (name, params = {}) => {
    if (!isAnalyticsEnabled()) return;
    if (analytics) logEvent(analytics, name, params);
};

const sendFb = (name, params = {}) => {
    if (!isAnalyticsEnabled()) return;
    if (isFbqAvailable()) {
        // 'trackCustom' for non-standard events. We don't need CAPI dedup for
        // these — promo events are funnel signals, not actual conversions.
        window.fbq('trackCustom', name, params);
    }
};

const sendPin = (name, params = {}) => {
    if (!isAnalyticsEnabled()) return;
    if (isPintrkAvailable()) {
        window.pintrk('track', 'custom', { event_name: name, ...params });
    }
};

/**
 * Fired once per session whenever a promo successfully resolves to active
 * (either via URL code, manual entry, or date window). Lets us measure
 * how often campaigns actually reach a visitor.
 */
export function trackPromoActive({ promoId, code, kind, discountPercent }) {
    const params = {
        promo_id: promoId,
        code: code || null,
        kind,
        discount_percent: discountPercent,
    };
    sendGa('promo_active', params);
    sendFb('PromoActive', params);
    sendPin('promo_active', params);
}

/**
 * Fired when the visitor types a code into the PromoCodeBar and hits
 * Apply, regardless of whether it matched. `matched` lets us measure
 * typo / expired-code rates per campaign.
 */
export function trackPromoCodeApplied({ code, matched, discountPercent }) {
    const params = {
        code: code || null,
        matched: Boolean(matched),
        discount_percent: discountPercent || 0,
    };
    sendGa('promo_code_applied', params);
    sendFb('PromoCodeApplied', params);
    sendPin('promo_code_applied', params);
}

/**
 * Fired when a Zazzle checkout link is opened with a promo applied. This
 * is the closest signal we have to a discounted intent-to-purchase, since
 * we never see the actual Zazzle order.
 */
export function trackPromoCheckoutClick({ promoId, code, discountPercent, productId, productKey }) {
    const params = {
        promo_id: promoId,
        code: code || null,
        discount_percent: discountPercent,
        product_id: productId,
        product_key: productKey,
    };
    sendGa('promo_checkout_click', params);
    sendFb('PromoCheckoutClick', params);
    sendPin('promo_checkout_click', params);
}
